import { useEffect, useState } from "react";
import { api, save } from "./api";
import type { EventData } from "./types";
type SyncStatus = {
  url: string;
  connected: boolean;
  lastSync: string | null;
  error: string;
  busy: boolean;
  events: { eventId: string; name: string; conflict: boolean }[];
  devices?: { id: string; name: string; enabled: number; eventIds: string[] }[];
};
export function Connections({ events }: { events: EventData[] }) {
  const [sync, setSync] = useState<SyncStatus | null>(null),
    [drive, setDrive] = useState<{
      configured: boolean;
      redirectUri: string;
      parentFolderId: string;
      parentFolderName: string;
    } | null>(null);
  const [url, setUrl] = useState(""),
    [key, setKey] = useState(""),
    [name, setName] = useState(""),
    [assigned, setAssigned] = useState<string[]>([]),
    [newKey, setNewKey] = useState("");
  const [parentFolder, setParentFolder] = useState("");
  const [clientId, setClientId] = useState(""),
    [secret, setSecret] = useState(""),
    [error, setError] = useState(""),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false);
  async function refresh() {
    const [s, d] = await Promise.all([
      api<SyncStatus>("/admin/sync"),
      api<{
        configured: boolean;
        redirectUri: string;
        parentFolderId: string;
        parentFolderName: string;
      }>("/admin/drive/status"),
    ]);
    setSync(s);
    setDrive(d);
    setParentFolder((old) => old || d.parentFolderId);
    setUrl((old) => old || s.url);
  }
  useEffect(() => {
    void refresh().catch((e) => setError(e.message));
    const timer = setInterval(() => void refresh().catch(() => {}), 15000);
    return () => clearInterval(timer);
  }, []);
  async function action(fn: () => Promise<void>) {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await fn();
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <div className="page-heading">
        <div>
          <p className="eyebrow">CONNECTED WHEN YOU NEED IT</p>
          <h1>Connections</h1>
          <p>
            Manage Google Drive and central event updates. Local events keep
            working offline.
          </p>
        </div>
      </div>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {message && <p role="status">{message}</p>}
      <div className="setup-grid">
        <section className="panel">
          <h2>Google Drive</h2>
          <p>
            {drive?.configured
              ? "Connected on this computer."
              : "Not connected on this computer yet."}
          </p>
          <p>
            Use a Google OAuth web client with the Drive API enabled. Add this
            authorized redirect URI in Google Cloud:
          </p>
          <code className="connection-url">{drive?.redirectUri}</code>
          <label>
            Google OAuth client ID
            <input
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              autoComplete="off"
            />
          </label>
          <label>
            Google OAuth client secret
            <input
              type="password"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              autoComplete="off"
            />
          </label>
          <button
            className="primary"
            disabled={busy || !clientId || !secret}
            onClick={() =>
              void action(async () => {
                const result = await save("/admin/drive/connect", {
                  clientId,
                  clientSecret: secret,
                });
                setSecret("");
                location.assign(result.url);
              })
            }
          >
            Connect Google Drive
          </button>
          <p>
            Complete Google’s consent screen, then create an event folder from
            Event setup. Each event laptop needs its own Google connection. Your
            Google password is entered only on Google’s page.
          </p>
          <hr />
          <h3>Parent folder for new events</h3>
          <p>
            Optionally put every newly created event folder inside one Drive
            folder. Paste its Google Drive folder link or ID. Leave blank to
            create event folders at the top level of Drive.
          </p>
          {drive?.parentFolderName && (
            <p role="status">Current parent: {drive.parentFolderName}</p>
          )}
          <label>
            Google Drive parent folder link or ID
            <input
              value={parentFolder}
              onChange={(e) => setParentFolder(e.target.value)}
              placeholder="https://drive.google.com/drive/folders/..."
              autoComplete="off"
            />
          </label>
          <button
            disabled={busy || !drive?.configured}
            onClick={() =>
              void action(async () => {
                await save("/admin/drive/parent", {
                  parentFolder,
                }, "PUT");
                setMessage(
                  parentFolder.trim()
                    ? "Parent folder saved. New event folders will be created inside it."
                    : "Parent folder cleared. New event folders will be created at the top level of Drive.",
                );
              })
            }
          >
            Save parent folder
          </button>
        </section>
        <section className="panel">
          <h2>Connect this event laptop</h2>
          <p>
            The central server distributes assigned events, account access and
            designs. Photos and watched-folder paths stay here.
          </p>
          <label>
            Central server URL
            <input
              type="url"
              placeholder="https://photowall.visra.ae"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
          </label>
          <label>
            Device key
            <input
              type="password"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              autoComplete="off"
            />
          </label>
          <div className="button-row">
            <button
              disabled={busy || !url || !key}
              onClick={() =>
                void action(async () => {
                  await save("/admin/sync/connect", { url, key });
                  setKey("");
                  setMessage("Connection saved. Check the sync status below.");
                })
              }
            >
              Connect & download events
            </button>
            <button
              disabled={busy || !sync?.connected}
              onClick={() =>
                void action(async () => {
                  await save("/admin/sync/pull", {});
                })
              }
            >
              Sync now
            </button>
          </div>
          <p>
            {sync?.busy
              ? "Syncing…"
              : sync?.lastSync
                ? `Last checked: ${new Date(sync.lastSync).toLocaleString()}`
                : "No successful sync yet."}
          </p>
          {sync?.error && <p className="error">{sync.error}</p>}
          {sync?.events
            .filter((e) => e.conflict)
            .map((e) => (
              <div className="sync-conflict" key={e.eventId}>
                <h3>{e.name}</h3>
                <p>
                  The server has an update, but this laptop also has
                  unsaved-to-server changes. Choose which version to keep. Local
                  photos and watched folders are preserved.
                </p>
                <div className="button-row">
                  <button
                    disabled={busy}
                    onClick={() =>
                      void action(async () => {
                        await save(`/admin/sync/events/${e.eventId}/resolve`, {
                          choice: "server",
                        });
                        setMessage(
                          "Server design applied. Reload the event editor.",
                        );
                      })
                    }
                  >
                    Use server version
                  </button>
                  <button
                    disabled={busy}
                    onClick={() =>
                      void action(async () => {
                        await save(`/admin/sync/events/${e.eventId}/resolve`, {
                          choice: "local",
                        });
                        setMessage(
                          "Local design kept. It has not been published to the central server.",
                        );
                      })
                    }
                  >
                    Keep local version
                  </button>
                </div>
              </div>
            ))}
          <small>
            Automatic checks run every 30 seconds. Edit published designs on the
            central server. Offline copies keep working while internet is
            unavailable.
          </small>
        </section>
        <section className="panel">
          <h2>Enroll event laptops</h2>
          <p>
            Use this section on your main server. Give each laptop its own
            device key and assigned events.
          </p>
          <label>
            Laptop name
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Event laptop 01"
            />
          </label>
          {events.map((e) => (
            <label className="check" key={e.id}>
              <input
                type="checkbox"
                checked={assigned.includes(e.id)}
                onChange={(x) =>
                  setAssigned((old) =>
                    x.target.checked
                      ? [...old, e.id]
                      : old.filter((id) => id !== e.id),
                  )
                }
              />
              {e.name}
            </label>
          ))}
          <button
            disabled={busy || !name || !assigned.length}
            onClick={() =>
              void action(async () => {
                const r = await save("/admin/sync/devices", {
                  name,
                  eventIds: assigned,
                });
                setNewKey(r.key);
                setMessage("Copy this device key now. It is shown only once.");
              })
            }
          >
            Create device key
          </button>
          {newKey && (
            <label>
              New device key
              <input readOnly value={newKey} />
              <small>
                Treat this like a password. Paste it only into the assigned
                event laptop.
              </small>
            </label>
          )}
          {sync?.devices?.map((d) => (
            <div className="button-row" key={d.id}>
              <span>
                {d.name} · {d.enabled ? "Active" : "Revoked"}
              </span>
              {!!d.enabled && (
                <button
                  disabled={busy}
                  onClick={() =>
                    void action(async () => {
                      await save(`/admin/sync/devices/${d.id}/revoke`, {});
                    })
                  }
                >
                  Revoke device key
                </button>
              )}
            </div>
          ))}
        </section>
      </div>
    </>
  );
}
